import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@clerk/react';
import { ExternalLink } from 'lucide-react';
import { salesforceApi } from '@client/api/integrations';
import { SALESFORCE_SETUP_GUIDE_URL } from '@client/util/constants';

export function SalesforceOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const advisorId = auth?.sessionClaims?.userId;
  const code = searchParams.get('code');
  const exchangedRef = useRef(false);

  const exchangeMutation = useMutation({
    mutationFn: (code: string) => {
      const codeVerifier = sessionStorage.getItem('salesforce_code_verifier') || undefined;
      sessionStorage.removeItem('salesforce_code_verifier');
      return salesforceApi.oauth.exchangeCode({ code, codeVerifier });
    },
    onSuccess: () => {
      if (advisorId) {
        navigate(`/advisors/${advisorId}/integrations`);
      } else {
        navigate('/');
      }
    },
  });

  useEffect(() => {
    if (code && auth.isLoaded && !exchangedRef.current) {
      exchangedRef.current = true;
      exchangeMutation.mutate(code);
    }
  }, [code, auth.isLoaded]);

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-800">No authorization code found. Please try connecting again.</p>
        </div>
      </div>
    );
  }

  if (exchangeMutation.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <p className="mb-2 text-lg font-semibold text-red-800">Connection Failed</p>
          <p className="text-sm text-red-700">
            {exchangeMutation.error instanceof Error
              ? exchangeMutation.error.message
              : 'Failed to connect to Salesforce'}
          </p>
          <a
            href={SALESFORCE_SETUP_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-600 mt-4 inline-flex items-center gap-1 text-sm font-medium"
          >
            View the Salesforce setup guide
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary" />
        <p className="text-gray-700">Connecting to Salesforce...</p>
      </div>
    </div>
  );
}
